import React, { useEffect, useState } from "react";
import { Bot, ShieldCheck, ChevronUp, ChevronDown } from "lucide-react";
import { useLang } from "@/lib/lang";

const CONSENT_KEY = "lechatceltique.ai_notice_accepted_v3";

const TXT = {
  fr: {
    short: "Personnages IA · divertissement & réflexion philosophique · des suggestions, jamais d'ordres · tes choix t'appartiennent",
    more: "Avertissement", less: "Réduire", full: "Avertissement complet",
    chat: "Réponse générée par une intelligence artificielle, à but de divertissement et de réflexion philosophique. Ce personnage n'est pas une personne réelle : il peut suggérer des pistes avec bienveillance et humour, mais n'impose rien, ne fixe aucun délai, ne prédit rien et t'accompagne vers ta propre réflexion par des questions ouvertes. Il ne remplace aucun avis médical, psychologique, juridique ou financier. Tes décisions t'appartiennent.",
    title: "Bienvenue au coin du feu",
    intro: "Ici, des voix venues des mythes celtes et grecs, des anges et des éléments t'accompagnent pour penser, rire, rêver et te retrouver. Avant d'entrer, quelques mots importants :",
    points: [
      ["Ce sont des intelligences artificielles", "Tous les personnages (bardes, druides, dieux, archanges, éléments…) sont des IA. Ce ne sont ni de vraies personnes, ni des voyants, ni des thérapeutes, ni des coachs certifiés."],
      ["Notre mission : la philosophie et l'autoréflexion", "Les personnages accompagnent sans diriger. Ils parlent par images, métaphores et poésie, suggèrent indirectement sans jamais imposer, posent des questions, éclairent avec des pensées philosophiques variées et dédramatisent avec une pointe de satire et de dérision poétique, pour t'aider à trouver tes propres réponses — vers plus de bien-être, d'autonomie, de détachement et de pleine conscience."],
      ["Comment ils t'accompagnent", "Par des questions ouvertes qui invitent à l'introspection et à te reconnecter à toi-même (corps, souffle, émotions) et à ton environnement. Toute action évoquée n'est qu'un exemple proposé « si le cœur t'en dit », sans aucun délai ni obligation. Un mot de réconfort sincère peut venir quand tu exprimes une peine ou un doute — jamais de flatterie ni de complaisance."],
      ["Pour le divertissement, pas pour décider à ta place", "Les réponses sont générées automatiquement et peuvent être inexactes. Elles ne remplacent jamais l'avis d'un professionnel (santé, psychologie, droit, finances) et ne prédisent pas l'avenir. Ton libre-arbitre reste souverain."],
      ["Gratuit, anonyme, sans inscription", "Aucun paiement, aucun compte, aucun courriel demandé. Les conversations sont chiffrées puis effacées automatiquement."],
      ["Si tu traverses un moment difficile", "Appelle le 3114 (prévention du suicide, France, gratuit, 24h/24) ou le 112. Un humain t'écoutera."],
    ],
    accept: "J'ai compris, entrer",
    read: "Lire l'avertissement & la confidentialité",
  },
  en: {
    short: "AI characters · entertainment & philosophical reflection · suggestions, never orders · your choices are yours",
    more: "Notice", less: "Collapse", full: "Full notice",
    chat: "Answer generated by an artificial intelligence, for entertainment and philosophical reflection. This character is not a real person: it may kindly suggest ideas with humour, but imposes nothing, sets no deadline, predicts nothing and accompanies you toward your own reflection through open questions. It never replaces medical, psychological, legal or financial advice. Your decisions are yours.",
    title: "Welcome by the fire",
    intro: "Here, voices from Celtic and Greek myths, angels and the elements accompany you to think, laugh, dream and find yourself. Before entering, a few important words:",
    points: [
      ["They are artificial intelligences", "Every character (bards, druids, gods, archangels, elements…) is an AI. They are not real people, psychics, therapists or certified coaches."],
      ["Our mission: philosophy and self-reflection", "The characters accompany without directing. They speak through images, metaphors and poetry, suggest indirectly without ever imposing, ask questions, shed light with varied philosophical thought and de-dramatise with gentle satire and poetic self-mockery, helping you find your own answers — toward more well-being, autonomy, detachment and mindfulness."],
      ["How they accompany you", "Through open questions inviting introspection and reconnection with yourself (body, breath, emotions) and your surroundings. Any action mentioned is only an example offered \"if your heart says so\", with no deadline or obligation. A sincere word of comfort may come when you express pain or doubt — never flattery or complacency."],
      ["For entertainment, not to decide for you", "Answers are generated automatically and may be inaccurate. They never replace professional advice (health, psychology, law, finance) and do not predict the future. Your free will stays sovereign."],
      ["Free, anonymous, no sign-up", "No payment, no account, no e-mail asked. Conversations are encrypted and automatically erased."],
      ["If you are going through a hard time", "Call your local emergency number (112 in Europe). A human will listen."],
    ],
    accept: "I understand, enter",
    read: "Read the notice & privacy",
  },
};

const NoticePoints = ({ t, compact }) => (
  <ul className={compact ? "space-y-2" : "space-y-3"}>
    {t.points.map(([h, d]) => (
      <li key={h} className="text-left">
        <div className="font-semibold" style={{ color: "#f5d572", fontSize: compact ? 11.5 : 13.5 }}>{h}</div>
        <div style={{ color: "var(--violet-soft)", fontSize: compact ? 11.5 : 13.5, lineHeight: 1.55 }}>{d}</div>
      </li>
    ))}
  </ul>
);

export const AiBanner = () => {
  const { lang } = useLang();
  const t = TXT[lang] || TXT.fr;
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="ai-banner" role="note" className="fixed bottom-0 inset-x-0 z-40"
         style={{ background: "rgba(6,4,14,0.92)", borderTop: "1px solid rgba(212,175,55,0.3)", color: "#e8dcb8", backdropFilter: "blur(12px)" }}>
      {open && (
        <div data-testid="ai-banner-details" className="max-w-4xl mx-auto px-5 pt-4 pb-2 max-h-[55vh] overflow-y-auto">
          <NoticePoints t={t} compact />
          <a href="/privacy#ia" data-testid="ai-banner-full-link" className="inline-block mt-3 text-[11px] font-mono underline" style={{ color: "#f5d572" }}>{t.full}</a>
        </div>
      )}
      <div className="flex items-center justify-center gap-2 px-3 py-1.5 text-[10.5px] sm:text-[11px] font-mono text-center">
        <Bot size={12} className="shrink-0" style={{ color: "#d4af37" }} />
        <span>{t.short}</span>
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid="ai-banner-toggle" aria-expanded={open}
                className="shrink-0 inline-flex items-center gap-1 underline" style={{ color: "#f5d572" }}>
          {open ? t.less : t.more} {open ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
        </button>
      </div>
    </div>
  );
};

export const ChatDisclaimer = ({ testId = "chat-ai-disclaimer", color }) => {
  const { lang } = useLang();
  const t = TXT[lang] || TXT.fr;
  return (
    <p data-testid={testId} className="mt-3 flex items-start gap-1.5 text-[11px] leading-snug"
       style={{ color: color || "rgba(232,220,184,0.6)" }}>
      <Bot size={12} className="shrink-0 mt-[1px]" />
      <span>{t.chat} <a href="/privacy#ia" className="underline">{t.full}</a></span>
    </p>
  );
};

export const AiConsentModal = () => {
  const { lang } = useLang();
  const t = TXT[lang] || TXT.fr;
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (window.location.pathname !== "/privacy" && !localStorage.getItem(CONSENT_KEY)) setOpen(true);
  }, []);
  if (!open) return null;
  const accept = () => { localStorage.setItem(CONSENT_KEY, new Date().toISOString()); setOpen(false); };
  return (
    <div data-testid="ai-consent-modal" role="dialog" aria-modal="true" aria-labelledby="ai-consent-title"
         className="fixed inset-0 z-[200] flex items-center justify-center p-4"
         style={{ background: "rgba(3,2,8,0.85)", backdropFilter: "blur(10px)" }}>
      <div className="mystic-card gold-border rounded-3xl p-6 sm:p-8 max-w-xl w-full max-h-[92vh] overflow-y-auto fade-up" style={{ background: "rgba(14,8,32,0.97)" }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
               style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.45)" }}>
            <Bot size={20} style={{ color: "#d4af37" }} />
          </div>
          <h2 id="ai-consent-title" className="font-serif text-3xl leading-tight" style={{ color: "var(--star)" }}>{t.title}</h2>
        </div>
        <p data-testid="ai-consent-intro" className="text-sm leading-relaxed mb-5" style={{ color: "var(--star)" }}>{t.intro}</p>
        <NoticePoints t={t} />
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button type="button" onClick={accept} data-testid="ai-consent-accept" className="btn-gold inline-flex items-center justify-center gap-2">
            <ShieldCheck size={14} /> {t.accept}
          </button>
          <a href="/privacy#ia" data-testid="ai-consent-privacy-link" className="btn-ghost inline-flex items-center justify-center !text-[11px]">{t.read}</a>
        </div>
      </div>
    </div>
  );
};
